import dayjs from 'dayjs';

function removeDays(date: string | Date, days: number) {
   return dayjs(date).subtract(days, 'days');
}
function addDays(date: string | Date, days: number) {
   return dayjs(date).add(days, 'days');
}
function getWeekday(date?: string | Date, index = 0) {
   return dayjs(date).weekday(index);
}
function isBetween(date: string | Date, startDate: string | Date, endDate: string | Date) {
   return dayjs(date).isBetween(dayjs(startDate), dayjs(endDate), 'day', '[]');
}
function getQuarter(date = new Date()) {
   return Math.floor(date.getMonth() / 3 + 1);
}

const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const longDays: { [key: string]: Array<string> } = {
   nl: ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'],
   en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
   fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'],
};
const shortDays: { [key: string]: Array<string> } = {
   nl: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'],
   en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
   fr: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
};

const longMonths: { [key: string]: Array<string> } = {
   nl: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
   en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
   fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
};

interface WeekdayOption {
   id: number;
   name: string;
}

function getWeekdayOptions(t: (key: string) => string): WeekdayOption[] {
   return [
      { id: 1, name: t('general.weekdays.monday') },
      { id: 2, name: t('general.weekdays.tuesday') },
      { id: 3, name: t('general.weekdays.wednesday') },
      { id: 4, name: t('general.weekdays.thursday') },
      { id: 5, name: t('general.weekdays.friday') },
      { id: 6, name: t('general.weekdays.saturday') },
      { id: 7, name: t('general.weekdays.sunday') },
   ];
}

/** ISO weekday used by the backend (1 = Monday, 7 = Sunday). Matches Carbon `isoWeekday()`. */
function getIsoWeekday(date?: string | Date): number {
   if (!date) return -1;

   return dayjs(date).isoWeekday();
}

/** UI column index aligned with the `weekdays` / `shortDays` arrays (0 = Monday, 6 = Sunday). */
function getWeekdayArrayIndex(date?: string | Date): number {
   if (!date) return -1;

   return fromIsoWeekday(getIsoWeekday(date));
}

function fromIsoWeekday(isoWeekday: number): number {
   return isoWeekday - 1;
}

function toIsoWeekday(weekdayArrayIndex: number): number {
   return weekdayArrayIndex + 1;
}

function matchesWeekdayArrayIndex(date: string | Date, weekdayArrayIndex: number): boolean {
   return getWeekdayArrayIndex(date) === weekdayArrayIndex;
}

function getTimetableDateForWeekdayIndex(from: string | Date, weekdayArrayIndex: number): string {
   const fromIndex = getWeekdayArrayIndex(from);

   return dayjs(from)
      .add(weekdayArrayIndex - fromIndex, 'day')
      .format('YYYY-MM-DD');
}

export {
   addDays,
   fromIsoWeekday,
   getIsoWeekday,
   getQuarter,
   getTimetableDateForWeekdayIndex,
   getWeekday,
   getWeekdayArrayIndex,
   getWeekdayOptions,
   isBetween,
   longDays,
   longMonths,
   matchesWeekdayArrayIndex,
   removeDays,
   shortDays,
   toIsoWeekday,
   weekdays,
};
export type { WeekdayOption };
